// ============================================================================
// utils_deck.ts
// Chargement/sauvegarde keymap, formatage et détection des bindings.
// v2.26 — passage à 12 slots + ajout rangée layout
// ============================================================================

import { KeyBinding, DeckKeyMap, DEFAULT_KEY_MAP, KEYMAP_STORAGE_KEY } from './types_deck';
import { SLOT_COUNT, SLOT_COUNT_PIP } from '../../contexts/MediaStreamContext';

// ─── Chargement / sauvegarde ──────────────────────────────────────────────────

// Raccourcis cloisonnés par chaîne ; à défaut, on reprend l'ancienne clé globale
// comme point de départ (migration, comme pour le mediaboard).
export const loadKeyMap = (channelUuid?: string | null): DeckKeyMap => {
  try {
    const raw = (channelUuid ? localStorage.getItem(`${KEYMAP_STORAGE_KEY}:${channelUuid}`) : null)
             ?? localStorage.getItem(KEYMAP_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as DeckKeyMap;
      return {
        video:   Array(SLOT_COUNT).fill(null).map((_, i)     => parsed.video?.[i]   ?? DEFAULT_KEY_MAP.video[i]   ?? null),
        preview: Array(SLOT_COUNT).fill(null).map((_, i)     => parsed.preview?.[i] ?? DEFAULT_KEY_MAP.preview[i] ?? null),
        cut:     [parsed.cut?.[0] ?? DEFAULT_KEY_MAP.cut[0] ?? null],
        image:   Array(SLOT_COUNT).fill(null).map((_, i)     => parsed.image?.[i]   ?? DEFAULT_KEY_MAP.image[i]   ?? null),
        sound:   Array(SLOT_COUNT).fill(null).map((_, i)     => parsed.sound?.[i]   ?? DEFAULT_KEY_MAP.sound[i]   ?? null),
        pip:     Array(SLOT_COUNT_PIP).fill(null).map((_, i) => parsed.pip?.[i]     ?? DEFAULT_KEY_MAP.pip[i]     ?? null),
        jingle:  Array(SLOT_COUNT).fill(null).map((_, i)     => parsed.jingle?.[i]  ?? DEFAULT_KEY_MAP.jingle[i]  ?? null),
        layout:  Array(SLOT_COUNT).fill(null).map((_, i)     => parsed.layout?.[i]  ?? DEFAULT_KEY_MAP.layout[i]  ?? null),
      };
    }
  } catch {}
  return DEFAULT_KEY_MAP;
};

export const saveKeyMap = (km: DeckKeyMap, channelUuid?: string | null): void => {
  try { localStorage.setItem(channelUuid ? `${KEYMAP_STORAGE_KEY}:${channelUuid}` : KEYMAP_STORAGE_KEY, JSON.stringify(km)); } catch {}
};

// ─── Conversion code → label lisible (fallback sans display) ─────────────────

const CODE_LABEL_MAP: Record<string, string> = {
  Backquote:    '`',
  Minus:        '-',
  Equal:        '=',
  BracketLeft:  '[',
  BracketRight: ']',
  Backslash:    '\\',
  Semicolon:    ';',
  Quote:        "'",
  Comma:        ',',
  Period:       '.',
  Slash:        '/',
  Space:        'Espace',
  Enter:        'Entrée',
  Backspace:    '⌫',
  Tab:          'Tab',
  Escape:       'Échap',
  Delete:       'Suppr',
  Insert:       'Inser',
  Home:         'Début',
  End:          'Fin',
  PageUp:       'PgUp',
  PageDown:     'PgDn',
  ArrowUp:      '↑',
  ArrowDown:    '↓',
  ArrowLeft:    '←',
  ArrowRight:   '→',
  CapsLock:     'Verr.Maj',
  PrintScreen:  'Impr.Écr',
  ScrollLock:   'Arr.Déf',
  Pause:        'Pause',
  NumLock:      'Verr.Num',
  NumpadEnter:    '⌨Entrée',
  NumpadAdd:      '⌨+',
  NumpadSubtract: '⌨-',
  NumpadMultiply: '⌨*',
  NumpadDivide:   '⌨/',
  NumpadDecimal:  '⌨.',
  Numpad0:        '⌨0',
  Numpad1:        '⌨1',
  Numpad2:        '⌨2',
  Numpad3:        '⌨3',
  Numpad4:        '⌨4',
  Numpad5:        '⌨5',
  Numpad6:        '⌨6',
  Numpad7:        '⌨7',
  Numpad8:        '⌨8',
  Numpad9:        '⌨9',
};

const codeToFallbackLabel = (code: string): string => {
  if (CODE_LABEL_MAP[code]) return CODE_LABEL_MAP[code];
  if (/^Digit(\d)$/.test(code))  return code.replace('Digit', '');
  if (/^Key([A-Z])$/.test(code)) return code.replace('Key', '');
  if (/^F\d{1,2}$/.test(code))   return code;
  return code;
};

export const formatKeyBinding = (kb: KeyBinding | null): string => {
  if (!kb) return '—';

  const parts: string[] = [];
  if (kb.ctrl)  parts.push('Ctrl');
  if (kb.alt)   parts.push('Alt');
  if (kb.shift) parts.push('Shift');

  let keyLabel: string;
  if (kb.display && kb.display.length === 1) {
    keyLabel = kb.display.toUpperCase();
  } else if (kb.display && kb.display.length > 1 && !/^\s/.test(kb.display)) {
    keyLabel = codeToFallbackLabel(kb.code);
  } else {
    keyLabel = codeToFallbackLabel(kb.code);
  }

  parts.push(keyLabel);
  return parts.join('+');
};

// ─── Détection ────────────────────────────────────────────────────────────────

export const bindingMatches = (kb: KeyBinding, e: KeyboardEvent): boolean =>
  kb.code  === e.code     &&
  kb.ctrl  === e.ctrlKey  &&
  kb.shift === e.shiftKey &&
  kb.alt   === e.altKey;

// ─── Détection de conflits ────────────────────────────────────────────────────

export const findConflict = (
  km: DeckKeyMap,
  kb: KeyBinding,
  excludeRow: keyof DeckKeyMap,
  excludeIdx: number,
): { row: keyof DeckKeyMap; idx: number } | null => {
  for (const row of Object.keys(km) as (keyof DeckKeyMap)[]) {
    for (let i = 0; i < km[row].length; i++) {
      if (row === excludeRow && i === excludeIdx) continue;
      const b = km[row][i];
      if (b && b.code === kb.code && b.ctrl === kb.ctrl && b.shift === kb.shift && b.alt === kb.alt)
        return { row, idx: i };
    }
  }
  return null;
};
